import { useState, useEffect, useCallback, useRef } from "react";
import toast from "react-hot-toast";
import { agentTenderService } from "../../services/agentTenderApi";
import { CATEGORY_PLAYERS } from "../../constants/agentTenderChoices";

// The agent's own tenders (internal view — club present, Rule 8) with full
// feed-parity controls (P9): the Mine endpoint now accepts the same
// category / recruiting_for / type_of_tender / sort params + pagination +
// tab_counts as the browse feed, so "My requests" filters/sorts identically.
export const useMyAgentTenders = () => {
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [closingId, setClosingId] = useState(null);

  const [activeCategory, setActiveCategory] = useState(CATEGORY_PLAYERS);
  const [filters, setFilters] = useState({
    recruiting_for: "",
    type_of_tender: "",
    sort_by: "newest",
  });
  const [tabCounts, setTabCounts] = useState({
    players: 0,
    coaches: 0,
    staffs: 0,
    others: 0,
  });
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    currentPage: 1,
  });
  const abortRef = useRef(null);

  const buildParams = useCallback(
    (page) => {
      const p = new URLSearchParams();
      p.append("page", page);
      if (activeCategory) p.append("category", activeCategory);
      if (filters.recruiting_for) p.append("recruiting_for", filters.recruiting_for);
      if (filters.type_of_tender) p.append("type_of_tender", filters.type_of_tender);
      if (filters.sort_by) p.append("sort", filters.sort_by);
      return p;
    },
    [activeCategory, filters],
  );

  const fetchMine = useCallback(
    async (page = 1) => {
      setLoading(true);
      setError(null);
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const res = await agentTenderService.getMine(buildParams(page), controller.signal);
        if (res.success) {
          setTenders(res.data);
          setPagination(res.pagination);
          if (res.tabCounts) setTabCounts(res.tabCounts);
        } else {
          setError(res.error || "Failed to load your requests.");
        }
      } catch (err) {
        if (err?.name === "CanceledError" || err?.name === "AbortError") return;
        setError("Failed to load your requests. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [buildParams],
  );

  useEffect(() => {
    fetchMine(1);
  }, [fetchMine]);

  const changeCategory = (category) => {
    if (category === activeCategory) return;
    setActiveCategory(category);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const applyFilters = (patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const closeTender = async (id) => {
    setClosingId(id);
    const res = await agentTenderService.close(id);
    setClosingId(null);
    if (res.success) {
      toast.success("Request closed.");
      setTenders((prev) =>
        prev.map((t) => (t.id === id ? { ...t, status: "expired" } : t)),
      );
      return true;
    }
    toast.error(res.error || "We couldn't close this request. Please try again.");
    return false;
  };

  return {
    tenders,
    loading,
    error,
    closingId,
    fetchMine,
    closeTender,
    activeCategory,
    changeCategory,
    filters,
    applyFilters,
    tabCounts,
    pagination,
  };
};
